import classNames from 'classnames'
import { ForwardedRef, forwardRef, useImperativeHandle, useRef, useState } from 'react'

import CloseIcon from '../assets/icons/close.svg?react'

interface DialogHandle<ReturnValue = unknown, OpeningProps = undefined> {
  open: (props: OpeningProps extends undefined ? void : OpeningProps) => Promise<ReturnValue>
  close: (result?: ReturnValue) => void
}

interface DialogProps {
  title?: string | React.ReactNode
  children?: React.ReactNode
  withCloseButton?: boolean | React.ReactNode
  withHeaderDelimiter?: boolean
  width?: 'tight' | 'normal' | 'mid-wide'  | 'wide' | 'x-wide' | 'xx-wide' | 'full'
  height?: 'auto' | 'full'
  headerButtons?: React.ReactNode[]
  customWidth?: string
    childrenPadding?: string
    // Extra classes on the <dialog> panel itself — lets a dialog override the
    // default radius or style its ::backdrop (Tailwind's `backdrop:` variant).
    panelClassName?: string
}

const Dialog = <ReturnValue,>(
  {
    title,
    children,
    withCloseButton,
    withHeaderDelimiter,
    width = 'normal',
    height = 'auto',
    headerButtons,
    customWidth,
      childrenPadding = 'p-8',
      panelClassName
  }: DialogProps,
  ref: ForwardedRef<DialogHandle<ReturnValue>>
) => {
  const dialog = useRef<HTMLDialogElement>(null)

  const [returnValue, setReturnValue] = useState<ReturnValue | undefined>(undefined)

  const [resolvePromise, setResolvePromise] = useState<(result?: ReturnValue) => void>()

  const open = async (): Promise<ReturnValue> => {
    dialog.current!.showModal()

    return new Promise((resolve) => {
      setResolvePromise(() => resolve)
    })
  }

  const close = (result?: ReturnValue) => {
    if (!dialog.current) {
      return
    }

    setReturnValue(result)

    dialog.current.close()
  }

  const handleClose = () => {
    resolvePromise?.(returnValue)
    setReturnValue(undefined)
  }

  useImperativeHandle(
    ref,
    () => ({
      open,
      close
    }),
    []
  )

  return (
    <div className="text-neutralBlack-600">
      <dialog
        className={classNames('rounded-xl outline-none shadow-lg w-full flex-col overflow-y-scroll no-scrollbar', panelClassName, {
          'max-w-sm': width === 'tight' && !customWidth,
          'max-w-md': width === 'normal' && !customWidth,
            'max-w-xl': width === 'mid-wide' && !customWidth,
            'max-w-2xl': width === 'wide' && !customWidth,
          'max-w-3xl': width === 'x-wide' && !customWidth,
          'max-w-4xl': width === 'xx-wide' && !customWidth,
          'max-w-7xl': width === 'full' && !customWidth,
          'h-full max-h-[max(calc(100vh-2rem),780px)]': height === 'full',
        })}
        style={customWidth ? { maxWidth: customWidth } : undefined}
        onClose={handleClose}
        ref={dialog}
      >
        <div
          className={classNames('flex justify-between', {
            'border-b border-neutralBlack-300': withHeaderDelimiter
          })}
        >
          {title && (
            <div className={classNames('pl-8 pr-4 py-6 flex-1 min-w-0')}>
              {typeof title === 'string' ? (
                <h3 className="text-xl font-medium pr-2 truncate">{title}</h3>
              ) : (
                title
              )}
            </div>
          )}

          {withCloseButton && (
            <div className="absolute top-0 right-0 ">
              {withCloseButton === true ? (
                <button
                  type="button"
                  className="mt-3 mr-5 p-4 opacity-50 hover:opacity-100 outline-none"
                  onClick={() => close()}
                >
                  <CloseIcon className="size-6" />
                </button>
              ) : (
                withCloseButton
              )}
            </div>
          )}
          {headerButtons && (
            <div className="flex-none flex gap-4 py-6 pr-4 flex-col md:flex-row">{headerButtons}</div>
          )}
        </div>
        <div className="flex-auto flex flex-col overflow-y-scroll no-scrollbar">
          <div className={childrenPadding +" flex-auto flex flex-col"}>{children}</div>
        </div>
      </dialog>
    </div>
  )
}

const ForwardedDialog = forwardRef(Dialog) as <ReturnValue = unknown, OpeningProps = undefined>(
  props: DialogProps & { ref?: ForwardedRef<DialogHandle<ReturnValue, OpeningProps>> }
) => ReturnType<typeof Dialog>

export { ForwardedDialog as Dialog }
